import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useFirestore } from '../contexts/FirestoreContext';
import { collection, query, where, onSnapshot } from 'firebase/firestore';

/**
 * Custom hook to count active incidents for the current organization
 * Excludes deleted and resolved incidents and provides real-time updates
 * 
 * @param {string} cmtId - Optional CMT ID to filter incidents to specific CMT only
 */
export const useActiveIncidentCount = (cmtId = null) => {
  const { currentUser } = useAuth();
  const { db } = useFirestore();
  const [activeIncidentCount, setActiveIncidentCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // 🐛 DEBUG: Track parameter changes
  useEffect(() => {
    // Parameter changed
  }, [cmtId]);

  useEffect(() => {
    // Debug log removed to prevent console spam

    // Early return if no user data
    if (!currentUser?.orgId || !db) {
      // Missing required data - silent return
      setActiveIncidentCount(0);
      setLoading(false);
      return;
    }

    // 🔧 SURGICAL FIX: If no CMT provided, show loading until we get one
    if (!cmtId) {
      // No CMT provided - silent return
      setActiveIncidentCount(0);
      setLoading(true);
      return;
    }

    // Setting up real-time listener

    // Set up real-time listener for incidents
    const incidentsRef = collection(db, 'c3_incidents');
    const queryConstraints = [
      where('orgId', '==', currentUser.orgId)
    ];

    // Add CMT filtering if specified
    if (cmtId) {
      queryConstraints.push(where('cmtId', '==', cmtId));
    }

    const q = query(incidentsRef, ...queryConstraints);

    const unsubscribe = onSnapshot(q, (snapshot) => {
      try {
        setError(null);
        
        // Get all incidents and filter out deleted/resolved ones
        const allIncidents = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        // Filter to include only active and stabilising incidents
        const activeIncidents = allIncidents.filter(incident => 
          incident.status === 'Active' || incident.status === 'Stabilising'
        );
        
        // Real-time update processed
        
        // Update count
        const count = activeIncidents.length;
        setActiveIncidentCount(count);

        // Count updated

      } catch (err) {
        console.error('❌ Error processing active incident count update:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }, (err) => {
      console.error('❌ Error in active incident count listener:', err);
      setError(err.message);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [currentUser?.orgId, cmtId, db]);

  return { activeIncidentCount, loading, error };
}; 